import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Trash, Coins, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const BundlesTab = () => {
  const [bundles, setBundles] = useState([]);
  const [myContent, setMyContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '', description: '', bundle_type: 'photo', price_tokens: 50,
    content_ids: [], preview_url: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/api/store/bundles/mine`, { withCredentials: true }),
        axios.get(`${API}/api/upload/my-content`, { withCredentials: true }),
      ]);
      setBundles(b.data);
      setMyContent(c.data.items || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleContent = (id) => {
    setForm(f => ({
      ...f,
      content_ids: f.content_ids.includes(id)
        ? f.content_ids.filter(x => x !== id)
        : [...f.content_ids, id],
    }));
  };

  const create = async () => {
    if (!form.title.trim() || form.content_ids.length === 0 || form.price_tokens < 1) {
      toast.error('Fill all fields and select at least 1 item');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/store/bundles`, form, { withCredentials: true });
      toast.success('Bundle created');
      setOpen(false);
      setForm({ title: '', description: '', bundle_type: 'photo', price_tokens: 50, content_ids: [], preview_url: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this bundle? Existing purchasers keep access.')) return;
    try {
      await axios.delete(`${API}/api/store/bundles/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="bundles-tab">
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-sm">{bundles.length} bundle{bundles.length !== 1 && 's'}</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 flex items-center gap-2"
              data-testid="create-bundle-btn">
              <Plus className="w-5 h-5" /> New Bundle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Bundle</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="bg-slate-800 border-white/10" data-testid="bundle-title" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-slate-800 border-white/10" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select value={form.bundle_type} onChange={e => setForm({ ...form, bundle_type: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2">
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div>
                  <Label>Price (tokens)</Label>
                  <Input type="number" min="1" value={form.price_tokens}
                    onChange={e => setForm({ ...form, price_tokens: parseInt(e.target.value) || 0 })}
                    className="bg-slate-800 border-white/10" data-testid="bundle-price" />
                </div>
              </div>

              <div>
                <Label>Include items ({form.content_ids.length} selected)</Label>
                <div className="mt-2 max-h-56 overflow-y-auto grid grid-cols-3 gap-2 bg-slate-950/50 p-2 rounded-lg">
                  {myContent.length === 0 && (
                    <p className="col-span-3 text-slate-500 text-sm text-center py-4">
                      Upload content first to build bundles.
                    </p>
                  )}
                  {myContent.map(item => (
                    <button key={item.id} type="button" onClick={() => toggleContent(item.id)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                        form.content_ids.includes(item.id) ? 'border-blue-500 ring-2 ring-blue-500/50' : 'border-transparent'
                      }`}>
                      {item.content_type === 'image' ? (
                        <img src={`${API}${item.media_url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-800 flex items-center justify-center text-slate-500">
                          🎬
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={create} disabled={saving}
                className="w-full py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50"
                data-testid="save-bundle-btn">
                {saving ? 'Saving…' : 'Create Bundle'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : bundles.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No bundles yet — group your best content and sell them together.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bundles.map(b => (
            <div key={b.id} className="glass-light rounded-2xl p-4" data-testid={`bundle-card-${b.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-white truncate">{b.title}</h4>
                  <p className="text-xs text-slate-500 capitalize">{b.bundle_type} · {b.item_count} items</p>
                </div>
                <button onClick={() => remove(b.id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg">
                  <Trash className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                <span className="flex items-center gap-1 text-amber-400 font-bold">
                  <Coins weight="fill" className="w-4 h-4" /> {b.price_tokens}
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-xs">
                  <Eye className="w-4 h-4" /> {b.purchases_count} sales
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BundlesTab;
